<template>
  <v-row justify="center" v-if="Object.keys(refundInfo).length != 0">
    <v-col cols="12" sm="10" md="10" lg="8" xl="6" align="center">
      <h2 class="title-underline" translate>
        {{ $t(["REFUND DETAILS"]) }}
      </h2>

      <h3>
        {{ $t(["OrderNumber_Label"]) }}
        {{ refundInfo.orderNumberId }}
      </h3>

      <h3>
        {{ $t(["PhoneNumber_Label"]) }}
        {{ refundInfo.orderData.phoneNumber }}
      </h3>

      <v-alert
        v-model="errorMsgAlert"
        close-text="Close Alert"
        class="alert-text my-5"
        color="error"
        dark
        dismissible
        ref="snackbarError"
      >
        {{ $t(errorMsg) }}
      </v-alert>

      <v-card class="card mb-5">
        <v-row justify="center">
          <v-col align="center">
            <v-simple-table class="px-5">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t(["Date"]) }}</th>
                    <th class="text-left">{{ $t(["Merchandise"]) }}</th>
                    <th class="text-left">{{ $t(["Donation"]) }}</th>
                    <th class="text-left">{{ $t(["Total Refund"]) }}</th>
                  </tr>
                </thead>

                <tbody>
                  <tr>
                    <td>
                      <b>
                        {{
                          $i18n.locale == "fr"
                            ? formatDt(refundInfo.dt)
                                .locale($i18n.locale)
                                .format("D MMMM, YYYY")
                            : formatDt(refundInfo.dt)
                                .locale($i18n.locale)
                                .format("MMMM D, YYYY")
                        }}
                      </b>
                    </td>
                    <td
                      class="secondary--text font-weight-bold"
                      v-html="
                        '(' +
                        $t('Currency', {
                          amount: refundInfo.amount.merchandise.toFixed(2),
                        }) +
                        ')'
                      "
                    />
                    <td
                      class="secondary--text font-weight-bold"
                      v-html="
                        '(' +
                        $t('Currency', {
                          amount: refundInfo.amount.donation.toFixed(2),
                        }) +
                        ')'
                      "
                    />
                    <td
                      class="secondary--text font-weight-bold"
                      v-html="
                        '(' +
                        $t('Currency', {
                          amount: refundInfo.amount.total.toFixed(2),
                        }) +
                        ')'
                      "
                    />
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-divider class="mx-5"></v-divider>

            <!-- <div placeholder="{{ 'Refund Requested' | translate }}"></div> -->
            <h3 align="center" class="my-3">
              {{ $t(["Refund Requested"]) }}
              <span
                class="secondary--text font-weight-bold"
                v-html="
                  '(' +
                  $t('Currency', {
                    amount: refundInfo.amount.total.toFixed(2),
                  }) +
                  ')'
                "
              />
            </h3>

            <v-divider class="mx-5"></v-divider>

            <div v-show="refundInfo.status == 'Pending'">
              <v-btn
                class="btn-text btn-multiline my-5"
                color="primary"
                width="250px"
                x-large
                :loading="approveRefundBtnLoading"
                @click="approveRefund()"
              >
                <span class="text-wrap" translate>
                  {{ $t(["Approve Refund"]) }}
                </span>
              </v-btn>

              <v-divider class="mx-5"></v-divider>

              <v-btn
                class="btn-text btn-multiline my-5"
                color="secondary"
                width="250px"
                x-large
                outlined
                translate
                :loading="declineRefundBtnLoading"
                @click="declineRefund()"
              >
                <span class="text-wrap" translate>
                  {{ $t(["Decline Refund"]) }}
                </span>
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-card class="card mb-5">
        <v-row justify="center" align="center">
          <v-col>
            <TotalSummaryTable :tableData="totalSummaryTableData" />
          </v-col>
        </v-row>
      </v-card>

      <v-card class="card mb-5">
        <v-row justify="center" align="center">
          <v-col>
            <h4 align="center" class="my-3" translate>
              {{ $t(["REFUND SUMMARY"]) }}
            </h4>

            <v-divider class="mx-5"></v-divider>

            <v-simple-table class="px-5">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left" translate>
                      {{ $t(["Requested Date"]) }}
                    </th>
                    <th class="text-left" translate>{{ $t(["Amount"]) }}</th>
                    <th class="text-left" translate>{{ $t(["Status"]) }}</th>
                    <th class="text-left" translate>
                      {{ $t(["Closed Date"]) }}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="item in refundInfo.orderRefunds != undefined
                      ? refundInfo.orderRefunds
                      : []"
                    :key="item.dt"
                  >
                    <td>
                      {{
                        $i18n.locale == "fr"
                          ? formatDt(item.dt)
                              .locale($i18n.locale)
                              .format("D MMMM, YYYY")
                          : formatDt(item.dt)
                              .locale($i18n.locale)
                              .format("MMMM D, YYYY")
                      }}
                    </td>
                    <td
                      class="secondary--text"
                      v-html="
                        '(' +
                        $t('Currency', {
                          amount: item.amount.total.toFixed(2),
                        }) +
                        ')'
                      "
                    />

                    <td>{{ $t(item.status) }}</td>
                    <td>
                      {{
                        item.closed
                          ? $i18n.locale == "fr"
                            ? formatDt(item.closed)
                                .locale($i18n.locale)
                                .format("D MMMM, YYYY")
                            : formatDt(item.closed)
                                .locale($i18n.locale)
                                .format("MMMM D, YYYY")
                          : ""
                      }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-divider class="mx-5"></v-divider>

            <!-- <div placeholder="{{ 'Total Refunded' | translate }}"></div> -->
            <h3 align="center" class="my-3">
              {{ $t(["Total Refunded"]) }}
              <span
                class="secondary--text"
                v-html="
                  '(' +
                  $t('Currency', {
                    amount: refundedTotal.toFixed(2),
                  }) +
                  ')'
                "
              />
            </h3>
          </v-col>
        </v-row>
      </v-card>

      <v-card
        class="card mb-5"
        v-show="JSON.stringify(refundInfo).indexOf('cart') > -1"
      >
        <h4 align="center" class="my-3" translate>
          {{ $t(["MERCHANDISE SUMMARY"]) }}
        </h4>

        <v-divider class="mx-5"></v-divider>
        <v-row justify="center" align="center">
          <v-col>
            <div v-show="JSON.stringify(refundInfo).indexOf('cart') > -1">
              <MerchandiseTable
                :orderCart="
                  refundInfo.orderData.cart != undefined
                    ? Object.values(JSON.parse(refundInfo.orderData.cart))
                    : []
                "
              />

              <v-divider class="mx-5"></v-divider>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <v-row class="mt-10" justify="center">
        <v-col cols="10" sm="8" xl="4">
          <v-btn
            class="btn-text btn-multiline"
            x-large
            block
            translate
            @click="backToRefundOverview()"
          >
            <span class="text-wrap" translate>
              {{ $t(["Back: Refund Overview"]) }}
            </span>
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { Auth } from "aws-amplify";
import moment from "moment";

import { useAuthenicationStore } from "../../store/authenication";
import { useRefundStore } from "../../store/refund";

import MerchandiseTable from "@/app/components/MerchandiseTable.vue";
import TotalSummaryTable from "@/app/components/TotalSummaryTable.vue";

export default {
  name: "RefundDetails",
  data: () => ({
    refundInfo: {},
    totalSummaryTableData: {},
    refundedMerchandise: 0,
    refundedDonation: 0,
    refundedTotal: 0,
    approveRefundBtnLoading: false,
    declineRefundBtnLoading: false,
    errorMsgAlert: false,
    errorMsg: "",
  }),
  components: {
    MerchandiseTable,
    TotalSummaryTable,
  },
  setup() {
    const authStore = useAuthenicationStore();
    const refundStore = useRefundStore();

    return {
      authStore,
      refundStore,
    };
  },
  mounted() {
    this.refundInfo =
      this.$route.params.data != undefined
        ? this.$route.params.data
        : this.refundStore.refundData;

    this.refundInfo.orderRefunds.forEach((refund) => {
      if (refund.status == "Approved") {
        this.refundedMerchandise += refund.amount.merchandise;
        this.refundedDonation += refund.amount.donation;
        this.refundedTotal += refund.amount.total;
      }
    });

    this.totalSummaryTableData = {
      merchAmount:
        this.refundInfo.orderData.merchTotal != undefined
          ? this.refundInfo.orderData.merchTotal
          : 0,
      merchRefund:
        this.refundedMerchandise != undefined ? this.refundedMerchandise : 0,
      merchTotal:
        (this.refundInfo.orderData.merchTotal != undefined
          ? this.refundInfo.orderData.merchTotal
          : 0) -
        (this.refundedMerchandise != undefined ? this.refundedMerchandise : 0),
      donationAmount:
        this.refundInfo.orderData.donationAmount != undefined
          ? this.refundInfo.orderData.donationAmount
          : 0,
      donationRefund:
        this.refundedDonation != undefined ? this.refundedDonation : 0,
      donationTotal:
        (this.refundInfo.orderData.donationAmount != undefined
          ? this.refundInfo.orderData.donationAmount
          : 0) -
        (this.refundedDonation != undefined ? this.refundedDonation : 0),
      type:
        JSON.stringify(this.refundInfo).indexOf("cart") > -1
          ? "Merchandise"
          : "",
    };
  },
  methods: {
    formatDt(dt) {
      return moment(new Date(dt));
    },

    async updateRefundStatus(refundData) {
      try {
        await Auth.currentSession().then(async (data) => {
          await this.refundStore
            .updateRefundStatusApi(
              {
                eventName: this.authStore.runSiteInfo.eventName,
                orderId: this.refundInfo.orderId,
                refundInfo: refundData,
              },
              data.idToken.jwtToken
            )
            .then((response) => {
              // console.log(response);

              if (response.success) {
                localStorage.removeItem("refund");

                this.$router.replace({
                  name: "RefundOverview",
                  params: {
                    success: true,
                    message:
                      refundData.status == "Approved"
                        ? this.$gettext("Successfully approved refund request")
                        : this.$gettext("Refund request has been declined"),
                  },
                });
              } else {
                throw response.message;
              }
            });
        });
      } catch (error) {
        this.errorMsgAlert = true;
        this.errorMsg = error.message == undefined ? error : error.message;

        this.$nextTick(() => {
          const el = this.$el.querySelector("snackbarError");
          this.$vuetify.goTo(-5, el);

          return;
        });
      }
    },
    async declineRefund() {
      this.declineRefundBtnLoading = true;

      await this.updateRefundStatus({
        closed: new Date(),
        status: "Declined",
        refundId: this.refundInfo.id,
        refundPassword: this.$cookies.get("tff_authenicateRefundLogin"),
      });

      this.declineRefundBtnLoading = false;
    },
    async approveRefund() {
      this.approveRefundBtnLoading = true;

      await this.updateRefundStatus({
        closed: new Date(),
        status: "Approved",
        refundId: this.refundInfo.id,
        refundPassword: this.$cookies.get("tff_authenicateRefundLogin"),
      });

      this.approveRefundBtnLoading = false;
    },
    backToRefundOverview() {
      localStorage.removeItem("refund");
      this.$router.replace("/refundOverview");
    },
  },
};
</script>

<style lang="scss" scoped></style>
